import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import EntradaComSenha from './EntradaComSenha';
import { LOGIN_COM_SENHA } from '../utils/loginComSenha';
import { destinoDoConsentimento } from '../utils/destinoDoConsentimento';
import './PaginaDeConsentimento.css';

/**
 * A tela em que ela autoriza o Claude ou o ChatGPT a ver os dados dela.
 *
 * ── Por que ela existe no FinTracking, e não no Supabase ─────────────────
 *
 * O Supabase é o servidor OAuth, mas a tela de consentimento é do aplicativo:
 * é aqui que a pessoa vê O QUE está autorizando, com as palavras de quem
 * conhece os dados. O endereço é `Site URL + Authorization Path`, configurado
 * no painel do Supabase como `/oauth/consent`.
 *
 * ── Ela NÃO é uma tela pública ───────────────────────────────────────────
 *
 * Diferente do convite, aqui só a dona da conta decide. Sem sessão não há o
 * que aprovar — o Supabase manda para o login e volta para cá.
 *
 * ── O peso dos dois botões é o mesmo ─────────────────────────────────────
 *
 * Mesma regra da tela de convite: um "recusar" pequeno e cinza embaixo
 * transformaria isto num consentimento fabricado. Se negar é mais difícil que
 * aprovar, o "sim" não quer dizer nada.
 */
interface Cliente { name?: string; client_name?: string; client_uri?: string }

interface Detalhes {
  authorization_id: string;
  redirect_uri: string;
  client: Cliente;
  scope: string;
}

/**
 * O nome que o cliente DECLAROU. Serve de rótulo, nunca de identidade: quem
 * identifica é o endereço de retorno — ver `utils/destinoDoConsentimento.ts`.
 */
const nomeDeclarado = (c: Cliente): string =>
  c.client_name || c.name || 'Um aplicativo de IA';

export const PaginaDeConsentimento: React.FC = () => {
  const { session, loading, signInWithGoogle } = useAuth();
  const [detalhes, setDetalhes] = useState<Detalhes | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Qual resposta está indo: só o botão dela se enche. */
  const [decidindo, setDecidindo] = useState<'sim' | 'nao' | null>(null);

  const id = new URLSearchParams(window.location.search).get('authorization_id');

  const carregar = useCallback(async () => {
    if (!id) return setErro('Falta o identificador da autorização no endereço.');
    const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(id);
    if (error) return setErro(error.message);
    /*
     * Duas respostas possíveis: pedir consentimento, ou já ter consentido
     * antes — aí o Supabase devolve para onde mandar a pessoa de volta.
     */
    if (data && 'redirect_url' in data) {
      window.location.href = data.redirect_url;
      return;
    }
    setDetalhes(data as Detalhes);
  }, [id]);

  useEffect(() => { if (session) void carregar(); }, [session, carregar]);

  const decidir = async (aprovar: boolean) => {
    if (!id) return;
    setDecidindo(aprovar ? 'sim' : 'nao');
    const { data, error } = aprovar
      ? await supabase.auth.oauth.approveAuthorization(id, { skipBrowserRedirect: true })
      : await supabase.auth.oauth.denyAuthorization(id, { skipBrowserRedirect: true });
    if (error) { setErro(error.message); setDecidindo(null); return; }
    window.location.href = data!.redirect_url;
  };

  const destino = destinoDoConsentimento(detalhes?.redirect_uri);

  if (loading) return <div className="pcon-fundo"><p className="pcon-carregando">Carregando…</p></div>;

  if (!session) {
    return (
      <div className="pcon-fundo">
        <div className="pcon-cartao">
          <h1 className="pcon-titulo">Entre para autorizar</h1>
          <p className="pcon-texto">
            Um aplicativo de IA está pedindo acesso aos seus dados do FinTracking.
            Entre na sua conta para decidir.
          </p>
          <button className="pcon-sim pcon-entrar" onClick={() => void signInWithGoogle()}>
            Entrar com o Google
          </button>
          {/* Quem tem só email e senha também precisa conseguir autorizar. */}
          {LOGIN_COM_SENHA && (
            <>
              <div className="login-ou" aria-hidden="true"><span>ou</span></div>
              <EntradaComSenha />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pcon-fundo">
      <div className="pcon-cartao">
        {erro ? (
          <>
            <h1 className="pcon-titulo">Não deu para abrir este pedido</h1>
            <p className="pcon-texto">{erro}</p>
            <p className="pcon-nota">
              Um pedido de autorização vale por pouco tempo. Tente conectar de
              novo pelo aplicativo de IA.
            </p>
          </>
        ) : !detalhes ? (
          <p className="pcon-carregando">Carregando o pedido…</p>
        ) : (
          <>
            <h1 className="pcon-titulo">
              {destino.tipo === 'conhecido'
                ? `Dar ao ${destino.quem} acesso ao seu FinTracking?`
                : 'Dar a este aplicativo acesso ao seu FinTracking?'}
            </h1>
            {destino.tipo === 'conhecido' && (
              <p className="pcon-dominio">O acesso vai para {destino.host}</p>
            )}
            {destino.tipo === 'este-aparelho' && (
              <p className="pcon-aviso">
                O acesso vai para um programa <strong>neste aparelho</strong> ({destino.host}),
                que se apresenta como “{nomeDeclarado(detalhes.client)}”. Só autorize se foi
                você que começou esta conexão agora, aqui.
              </p>
            )}
            {destino.tipo === 'desconhecido' && (
              <p className="pcon-aviso">
                O acesso vai para <strong>{destino.host}</strong>, que se apresenta
                como “{nomeDeclarado(detalhes.client)}”. Esse <strong>não</strong> é um
                endereço do Claude nem do ChatGPT. Só autorize se você conhece esse
                endereço e foi você que começou esta conexão agora.
              </p>
            )}
            {destino.tipo === 'ilegivel' && (
              <p className="pcon-aviso">
                Este pedido não diz para onde o acesso vai, então não dá para
                autorizar. Tente conectar de novo pelo aplicativo de IA.
              </p>
            )}

            {/*
              O que sai, em vez do nome dos escopos.

              "openid profile email" não diz nada a quem vai decidir. O que
              importa é que os números dela vão sair daqui toda vez que ela
              perguntar algo lá — e que nada é apagado.
            */}
            <ul className="pcon-lista">
              <li>Pode <strong>ler</strong> seus lançamentos, saldos, projeções, dívidas, metas e categorias.</li>
              <li>Seus dados vão para o aplicativo de IA a cada pergunta que você fizer lá.</li>
              <li>Pode <strong>lançar e corrigir</strong> registros quando você pedir, confirmando antes de cada gravação.</li>
              <li><strong>Não apaga nada</strong>, nunca.</li>
              <li>Você pode desfazer quando quiser, no menu do FinTracking.</li>
            </ul>

            <div className="pcon-botoes">
              {/* Sem destino legível não há o que autorizar: sobra só o "não". */}
              {destino.tipo !== 'ilegivel' && (
                <button className="pcon-sim" disabled={decidindo !== null} aria-busy={decidindo === 'sim'} onClick={() => void decidir(true)}>
                  Autorizar
                </button>
              )}
              <button className="pcon-nao" disabled={decidindo !== null} aria-busy={decidindo === 'nao'} onClick={() => void decidir(false)}>
                Agora não
              </button>
            </div>

            <p className="pcon-nota">
              Autorizando como <strong>{session.user.email}</strong>.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaginaDeConsentimento;
